import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useAccess } from './api/data';
import { Icon, IconButton } from './ui';
import './settings-shell.css';

export interface SettingsShellProps {
  children: ReactNode;
  onClose: () => void;
  path: string;
}

function matchesPath(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

export function isSettingsPath(path: string): boolean {
  return path.startsWith('/account/') || matchesPath(path, '/admin');
}

const personalLinks = [
  { to: '/account/appearance', label: 'Оформление' },
  { to: '/account/settings', label: 'Безопасность' },
  { to: '/account/notifications', label: 'Уведомления' },
] as const;

export function SettingsShell({ children, onClose, path }: SettingsShellProps) {
  const access = useAccess();
  const navId = useId();
  const sidebarRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const [drawerPath, setDrawerPath] = useState<string | null>(null);
  const drawerOpen = drawerPath === path;
  const workspaceTarget = {
    entityType: 'Workspace',
    entityId: access.data?.account.workspaceId,
  };
  const canReadWorkspace = access.can('workspace.read', workspaceTarget);
  const canManageAccess = access.can('permissions.manage', workspaceTarget);
  const canReadAccounts = access.can('accounts.read', workspaceTarget);
  const closeDrawer = () => setDrawerPath(null);

  useEffect(() => {
    if (!drawerOpen) return;
    const sidebar = sidebarRef.current;
    if (!sidebar) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    sidebar.querySelector<HTMLElement>('[aria-current="page"], button')?.focus();

    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setDrawerPath(null);
        return;
      }
      if (event.key !== 'Tab') return;
      const controls = Array.from(
        sidebar.querySelectorAll<HTMLElement>('a[href], button:not(:disabled)'),
      );
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    const media = window.matchMedia('(min-width: 768px)');
    const resize = () => {
      if (media.matches) setDrawerPath(null);
    };
    document.addEventListener('keydown', keydown);
    media.addEventListener('change', resize);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', keydown);
      media.removeEventListener('change', resize);
      toggleRef.current?.focus({ preventScroll: true });
    };
  }, [drawerOpen]);

  return (
    <div className={`settings-shell${drawerOpen ? ' settings-drawer-open' : ''}`}>
      <aside
        ref={sidebarRef}
        id={navId}
        className="settings-sidebar"
        role={drawerOpen ? 'dialog' : undefined}
        aria-modal={drawerOpen ? true : undefined}
        aria-label="Разделы настроек"
      >
        <header className="settings-sidebar-heading">
          <IconButton
            icon="Cross"
            label={drawerOpen ? 'Закрыть разделы настроек' : 'Закрыть настройки'}
            onClick={drawerOpen ? closeDrawer : onClose}
          />
          <span>Настройки</span>
        </header>
        <nav className="settings-nav" aria-label="Настройки">
          {canReadWorkspace || canManageAccess || canReadAccounts ? (
            <section className="settings-nav-group" aria-labelledby={`${navId}-system`}>
              <h2 id={`${navId}-system`} className="settings-nav-group-label">
                Системные настройки
              </h2>
              {canReadWorkspace ? (
                <Link
                  to="/admin/workspace"
                  className="settings-nav-link"
                  aria-current={matchesPath(path, '/admin/workspace') ? 'page' : undefined}
                  onClick={closeDrawer}
                >
                  Рабочее пространство
                </Link>
              ) : null}
              {canManageAccess ? (
                <Link
                  to="/$section/$resource"
                  params={{ section: 'admin', resource: 'permission-groups' }}
                  className="settings-nav-link"
                  aria-current={matchesPath(path, '/admin/permission-groups') ? 'page' : undefined}
                  onClick={closeDrawer}
                >
                  Управление доступом
                </Link>
              ) : null}
              {canReadAccounts ? (
                <Link
                  to="/$section/$resource"
                  params={{ section: 'admin', resource: 'accounts' }}
                  className="settings-nav-link"
                  aria-current={matchesPath(path, '/admin/accounts') ? 'page' : undefined}
                  onClick={closeDrawer}
                >
                  Учётные записи
                </Link>
              ) : null}
            </section>
          ) : null}
          <section className="settings-nav-group" aria-labelledby={`${navId}-personal`}>
            <h2 id={`${navId}-personal`} className="settings-nav-group-label">
              Личные настройки
            </h2>
            {personalLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="settings-nav-link"
                aria-current={
                  matchesPath(path, link.to) ||
                  (link.to === '/account/settings' && path.startsWith('/account/security/'))
                    ? 'page'
                    : undefined
                }
                onClick={closeDrawer}
              >
                {link.label}
              </Link>
            ))}
          </section>
        </nav>
      </aside>
      {drawerOpen ? (
        <button
          type="button"
          className="settings-drawer-backdrop"
          aria-label="Закрыть разделы настроек"
          tabIndex={-1}
          onClick={closeDrawer}
        />
      ) : null}
      <div className="settings-content" inert={drawerOpen ? true : undefined}>
        <header className="settings-mobile-heading">
          <IconButton icon="Cross" label="Закрыть настройки" onClick={onClose} />
          <span>Настройки</span>
          <button
            ref={toggleRef}
            type="button"
            className="settings-menu-toggle"
            aria-label="Разделы настроек"
            aria-controls={navId}
            aria-expanded={drawerOpen}
            onClick={() => setDrawerPath(path)}
          >
            <Icon name="Menu" />
            <span>Разделы настроек</span>
          </button>
        </header>
        <main className="settings-main">{children}</main>
      </div>
    </div>
  );
}
